import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import get from 'lodash/get';
import SubscriptionServiceUnavailable from '../../components/subscription/form/SubscriptionServiceUnavailable';
import appendPromisfiedScript from '../../utils/dom/appendPromisfiedScript';
import trackingLog from '../../constants/payment/tracking';
import { providers } from '../../utils/subscription/getSubscriptionProvider';
import { logClientError, logClientInfo } from '../../actions/logging';

const RECURLYJS_SCRIPT_URL = 'https://js.recurly.com/v4/recurly.js';
const RECURLY_ERROR_MESSAGE = 'recurly.js is not on the window';

export default function ensureRecurlyJs(RecurlyComponent) {
  class EnsureRecurlyJs extends Component {
    static propTypes = {
      recurlyLogging: PropTypes.object.isRequired,
      setRecurlyErrorState: PropTypes.func.isRequired,
    };

    constructor(props) {
      super(props);

      this.state = {
        needToLoadRecurlyJs: true,
      };

      this.loadRecurlyJs = this.loadRecurlyJs.bind(this);
      this.checkForRecurlyJs = this.checkForRecurlyJs.bind(this);
      this.logRecurlyJsLoadError = this.logRecurlyJsLoadError.bind(this);
      this.logRecurlyJsScriptLoadTime =
        this.logRecurlyJsScriptLoadTime.bind(this);
    }

    componentDidMount() {
      const recurlyjs = window.recurly;

      if (!recurlyjs) {
        this.loadRecurlyJs();
      } else {
        // eslint-disable-next-line react/no-did-mount-set-state
        this.setState({
          recurly: recurlyjs,
          needToLoadRecurlyJs: false,
        });
      }
    }

    loadRecurlyJs() {
      const start = Date.now();
      appendPromisfiedScript(RECURLYJS_SCRIPT_URL)
        .catch((error) => error)
        .then((error) => {
          this.checkForRecurlyJs(error);
          this.logRecurlyJsScriptLoadTime(start);
        });
    }

    checkForRecurlyJs(error) {
      const { setRecurlyErrorState } = this.props;
      const recurlyjs = window.recurly;
      this.setState({
        recurly: recurlyjs,
        needToLoadRecurlyJs: false,
      });

      if (recurlyjs) {
        setRecurlyErrorState(false);
      } else {
        setRecurlyErrorState(true);
        const recurlyError = error || new Error(RECURLY_ERROR_MESSAGE);
        this.logRecurlyJsLoadError(recurlyError);
      }
    }

    logRecurlyJsLoadError(error) {
      this.props.recurlyLogging.logClientError({
        message: `${trackingLog.labels.paymentFormError}.${providers.recurly}js::not-loaded`,
        context: {
          label: trackingLog.labels.paymentFormError,
          stack: get(error, 'stack'),
          errorMessage: get(error, 'message'),
        },
      });
    }

    logRecurlyJsScriptLoadTime(start) {
      const time = Date.now() - start;
      this.props.recurlyLogging.logClientInfo({
        message: `${trackingLog.labels.libraryLoadTime}.${providers.recurly}js::${time}ms`,
        context: {
          label: trackingLog.labels.libraryLoadTime,
          time,
        },
      });
    }

    render() {
      const { needToLoadRecurlyJs, recurly } = this.state;
      if (needToLoadRecurlyJs) {
        return null;
      }
      // If recurly.js was not found on the window and it could not be loaded via script
      // then show the 'Subscription Unavailable' messaging.
      if (!recurly) {
        return <SubscriptionServiceUnavailable />;
      }
      return <RecurlyComponent {...this.props} recurly={recurly} />;
    }
  }

  function mapDispatchToProps(dispatch) {
    return {
      recurlyLogging: bindActionCreators(
        {
          logClientError,
          logClientInfo,
        },
        dispatch,
      ),
    };
  }

  return connect(null, mapDispatchToProps)(EnsureRecurlyJs);
}
